import clsx from 'clsx';
import { AlertTriangle, AlertOctagon, Info } from 'lucide-react';
import type { BuildDerived, BuildState, BuildWarning } from '@/types';
import { buildWarnings } from '@/lib/warnings';
import { Card } from '@/components/ui/Card';

interface WarningsPanelProps {
  state: BuildState;
  derived: BuildDerived;
}

const ICON = {
  critical: AlertOctagon,
  caution: AlertTriangle,
  info: Info,
} as const;

const colorClass = {
  rose:    'bg-rose-950/40    border-rose-800/40    text-rose-300',
  amber:   'bg-amber-950/40   border-amber-800/40   text-amber-300',
  emerald: 'bg-emerald-950/40 border-emerald-800/40 text-emerald-300',
  sky:     'bg-sky-950/40     border-sky-800/40     text-sky-300',
  zinc:    'bg-zinc-950/40    border-zinc-800/40    text-zinc-300',
} as const;

const iconColor = {
  rose: 'text-rose-400',
  amber: 'text-amber-400',
  emerald: 'text-emerald-400',
  sky: 'text-sky-400',
  zinc: 'text-zinc-400',
} as const;

export function WarningsPanel({ state, derived }: WarningsPanelProps) {
  const warnings = buildWarnings(state, derived);
  const critical = warnings.filter((w) => w.level === 'critical').length;
  const caution = warnings.filter((w) => w.level === 'caution').length;

  return (
    <Card padding="lg">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-semibold">Build Review</h2>
        <div className="flex items-center gap-3 text-xs font-mono">
          {critical > 0 && (
            <span className="text-rose-400">{critical} critical</span>
          )}
          {caution > 0 && (
            <span className="text-amber-400">{caution} caution</span>
          )}
          {critical === 0 && caution === 0 && (
            <span className="text-emerald-400">All checks pass</span>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {warnings.map((w) => <WarningRow key={w.id} w={w} />)}
      </div>
    </Card>
  );
}

function WarningRow({ w }: { w: BuildWarning }) {
  const Icon = ICON[w.level];
  return (
    <div
      className={clsx(
        'flex gap-3 p-3 rounded-md border text-sm',
        colorClass[w.color],
      )}
    >
      <Icon size={16} className={clsx('shrink-0 mt-0.5', iconColor[w.color])} />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-zinc-100">{w.title}</div>
        <div className="text-zinc-300 text-[13px] leading-snug mt-0.5">{w.detail}</div>
      </div>
    </div>
  );
}
